// LA PAGE D'ACCUEIL (www).
//
// Aucun composant à elle (demande du 2026-11-01 : « do not hallucinate components for the landing
// page, it will create a drift in identity »). La barre d'onglets est celle de Preview / Files /
// Data, la boîte de saisie celle du chat, les parties celles du plan, le badge de version celui du
// tableau de bord, les forfaits ceux de l'écran Compte. Les données montrées sont celles d'un vrai
// build : moodboard, le 2026-10-31 (son brief, sa première question, son plan en cinq parties).
// Tenu par lib/landing_wiring.test.ts.
import { useState, type ReactNode } from "react";
import { ChatComposer, StripCard } from "../components/Workspace";
import { Logo } from "../components/Logo";
import { PillTabs } from "../components/PillTabs";
import PlanCards from "../components/PlanCards";
import { ideaSignInHref } from "../lib/pending_idea";
import type { StripPart } from "../lib/plan_strip";
import { LANDING_PLANS } from "./plans";

const CONSOLE = "https://app.getperimetr.com";

type Tab = "build" | "how" | "safe" | "pricing";

// moodboard, 2026-10-31 : ce que l'opérateur a écrit, ce que l'agent a demandé, le plan qu'il a fait.
const BRIEF = "I'm a designer. I want an app where I can sign up, create moodboards, upload images and move and resize them freely on a big canvas, add short text notes, and share a board with a client.";
const QUESTION = {
  question: "What should the home screen show right after you sign up, before any boards exist yet?",
  options: ["A big friendly welcome message is enough", "Show a starter grid of empty 'board' placeholders to hint at what's coming", "Show quick tips on how the app will work", "Just a minimal, clean home page with my name"],
  multiSelect: false,
};
const PARTS: StripPart[] = [
  { id: "p1", index: 1, title: "I sign up and land on my account", state: "sealed", ms: null },
  { id: "p2", index: 2, title: "I create a moodboard and open it", state: "sealed", ms: null },
  { id: "p3", index: 3, title: "I upload images and move and resize them on a big canvas", state: "sealed", ms: null },
  { id: "p4", index: 4, title: "I add short text notes to the board", state: "todo", ms: null },
  { id: "p5", index: 5, title: "I share a board with a client and they leave comments", state: "todo", ms: null },
];

// Ce qui arrive à un changement, dans les cartes du plan : ce que l'opérateur voit pendant un build.
const SAFE_STEPS: StripPart[] = [
  { id: "s1", index: 1, title: "Your change, built on a copy", state: "sealed", ms: null },
  { id: "s2", index: 2, title: "Tested in a real browser", state: "sealed", ms: null },
  { id: "s3", index: 3, title: "You publish", state: "running", ms: null },
];

/** Une étape : un titre, une ligne, et la vraie pièce de la console, en plus grand. */
function Step({ title, line, children }: { title: string; line: string; children: ReactNode }) {
  return (
    <div className="grid items-center gap-6 text-left md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:gap-12">
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-semibold tracking-tight">{title}</h3>
        <p className="text-sm leading-relaxed text-text-secondary">{line}</p>
      </div>
      <div className="pointer-events-none select-none [zoom:1.2]" aria-hidden="true">{children}</div>
    </div>
  );
}

export default function Landing() {
  const [tab, setTab] = useState<Tab>("build");
  const [text, setText] = useState("");
  const signIn = (idea: string) => { location.href = ideaSignInHref(CONSOLE, idea); };
  const noop = () => {};

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur supports-[backdrop-filter]:bg-bg/70">
        <div className="mx-auto grid w-full max-w-5xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-4 py-3 sm:grid-cols-[1fr_auto_1fr] sm:gap-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <Logo className="h-7 w-7 text-accent" />
            <span className="hidden text-lg font-semibold tracking-tight sm:inline"><span className="text-accent">get</span>perimetr</span>
          </div>
          <PillTabs
            label="Sections"
            value={tab}
            onChange={(t) => { setTab(t); window.scrollTo({ top: 0 }); }}
            items={[["build", "Build"], ["how", "How it works"], ["safe", "Safe changes"], ["pricing", "Pricing"]]}
          />
          <a href={`${CONSOLE}/login`} className="justify-self-end whitespace-nowrap text-sm text-text-secondary hover:text-text">Sign in</a>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center gap-14 px-6 py-16 text-center">
        {tab === "build" && (
          <section className="flex w-full max-w-3xl flex-1 flex-col items-center justify-center gap-8 pb-16">
            <div className="flex flex-col gap-3">
              <h1 className="text-5xl font-semibold tracking-tight sm:text-6xl">Lean to build. Built to last.</h1>
              <p className="mx-auto max-w-md text-base leading-relaxed text-text-secondary">
                Describe the app you need. getperimetr builds it, tests it in a browser and puts it online.
              </p>
            </div>
            <div className="w-full text-left">
              <ChatComposer size="compact" mode="act" text={text} onTextChange={setText} onSend={() => signIn(text)} />
            </div>
          </section>
        )}

        {tab === "how" && (
          <section className="flex w-full flex-col gap-20 pb-16">
            <Step title="You describe it." line="In your own words, in any language.">
              <ChatComposer size="compact" mode="act" text={BRIEF} onTextChange={noop} />
            </Step>
            <Step title="It asks what matters." line="One question at a time, before it builds.">
              <ChatComposer size="compact" mode="act" text="" onTextChange={noop} askPending={QUESTION} onPickOption={noop} />
            </Step>
            <Step title="It builds in parts." line="Each part is tested in a real browser before the next.">
              <div className="flex flex-col items-start gap-2 rounded-2xl border border-border bg-surface p-5">
                {PARTS.map((p) => <StripCard key={p.id} part={p} startable={false} suggested={false} onStart={noop} />)}
              </div>
            </Step>
          </section>
        )}

        {tab === "safe" && (
          <section className="flex w-full flex-1 flex-col justify-center pb-16">
            <div className="grid items-center gap-10 text-left md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-16">
              <div className="flex flex-col gap-4">
                <h2 className="text-4xl font-semibold tracking-tight">Your users never see a broken app.</h2>
                <p className="text-base leading-relaxed text-text-secondary">
                  Every change is built on a private copy. Your users keep the app they know until the change passes its tests and you publish it.
                </p>
                <ul className="flex flex-col gap-2 text-sm font-medium">
                  <li><span className="text-ok">✓</span> Backed up every night</li>
                  <li><span className="text-ok">✓</span> Any earlier version can come back</li>
                </ul>
              </div>
              <div className="pointer-events-none select-none [zoom:1.3]" aria-hidden="true">
                <div className="flex flex-col items-start gap-2 rounded-2xl border border-border bg-surface p-5">
                  {SAFE_STEPS.map((p) => <StripCard key={p.id} part={p} startable={false} suggested={false} onStart={noop} />)}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === "pricing" && (
          <section className="flex w-full flex-col items-center gap-10 pb-16">
            <div className="flex flex-col gap-2">
              <h2 className="text-4xl font-semibold tracking-tight">Start free. Then €5 a month.</h2>
              <p className="text-sm text-text-secondary">A free month for one app, no card needed. Then hosting, database and backups for two apps. Your apps stay online when your credit runs out.</p>
            </div>
            <div className="w-full max-w-5xl">
              <PlanCards plans={LANDING_PLANS} view={{ plan: null }} busy={null} roomy onPick={() => signIn(text)} />
            </div>
          </section>
        )}
      </main>

    </div>
  );
}
